import React from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useJitsi } from "../../contexts/VideoCallContext";

interface CallEndedScreenProps {
  onClose?: () => void;
}

const CallEndedScreen: React.FC<CallEndedScreenProps> = ({ onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { rejoinCall, closeJitsi, roomName, displayName } = useJitsi();

  const handleRejoin = () => {
    rejoinCall();
    // No navigation needed since we're using iframe-only approach
  };

  const handleReturnToCalendar = () => {
    closeJitsi();
    navigate("/ehr/scheduling");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        backgroundColor: "#f8f9fa",
        padding: 3,
        textAlign: "center",
      }}
    >
      {/* Main message */}
      <Typography
        variant="h6"
        sx={{
          color: "#1f2937",
          fontWeight: 600,
          fontSize: "18px",
          marginBottom: 3,
          lineHeight: 1.4,
        }}
      >
        You've left the appointment
      </Typography>

      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          width: "100%",
          maxWidth: "300px",
        }}
      >
        {/* Rejoin button */}
        <Button
          variant="outlined"
          onClick={handleRejoin}
          sx={{
            flex: 1,
            py: 1.5,
            px: 3,
            borderRadius: "8px",
            borderColor: "#d1d5db",
            color: "#374151",
            fontWeight: 500,
            fontSize: "14px",
            textTransform: "none",
            "&:hover": {
              borderColor: "#9ca3af",
              backgroundColor: "#f9fafb",
            },
            "&:focus": {
              outline: "2px solid #3b82f6",
              outlineOffset: "2px",
            },
          }}
        >
          Rejoin
        </Button>

        {/* Return to calendar button */}
        <Button
          variant="contained"
          onClick={handleReturnToCalendar}
          sx={{
            flex: 1,
            py: 1.5,
            px: 3,
            borderRadius: "8px",
            backgroundColor: "#0ea5e9",
            color: "white",
            fontWeight: 500,
            fontSize: "14px",
            textTransform: "none",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            "&:hover": {
              backgroundColor: "#0284c7",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            },
            "&:focus": {
              outline: "2px solid #3b82f6",
              outlineOffset: "2px",
            },
            "&:active": {
              backgroundColor: "#0369a1",
            },
          }}
        >
          Return to calendar
        </Button>
      </Box>
    </Box>
  );
};

export default CallEndedScreen;
